import { Modal } from '@mui/material';
import { useMemo, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { AppButton } from '../AppButton/AppButton';
import emptyListImg from '../../../assets/images/emptyList.png';
import { X, ChevronDown, Download, Send } from 'react-feather';
import {
  Timer,
  CheckCircle,
  Warning,
  ArrowRight,
  ArrowDownLeft,
  ArrowURightDown,
  CaretUp,
  CaretDown,
  File,
  ArrowBendDoubleUpLeft,
  ArrowSquareOut,
} from 'phosphor-react';
import './AppPaidInvoicesModal.scss';
import { COLORS } from '../../../utils/colors';
import { InvoiceType } from '../../modules/payments/BuyerPaymentTable/BuyerPaymentTable';
import {
  DillTransaction,
  formatMoney,
  getEstimatedWithdrawArrival,
  getNextBusinessDay,
  Supplier,
  InvoicePaymentDetails,
  appDelay,
  groupUsedCreditsByDocNumber,
  DillCreditUsed,
} from '@dill/dill-shared';
import AppTransactionStatusPill from '../AppTransactionStatusPill/AppTransactionStatusPill';
import { useNavigate } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { useParamMainBuyerId } from '../../../utils/hooks/useParamMainBuyerId';
import {
  downloadInvoicePdfReceipt,
  downloadMultiInvoicePdfZip,
  getInvoicePdf,
} from '../../../redux/services/buyersService';
import { getFirstLetters, openDillToast } from '../../../utils/helpers';
import { REACT_APP_FRONTEND_URL } from '../../../utils/constants';
import { ResendReceiptModal } from '../ResendReceiptModal/ResendReceiptModal';
import { useWindowActualWidth } from '../../../utils/domTools';
import {
  getTransactionById,
  refundFiservTransaction,
} from '../../../redux/services/paymentsService';
import { openMessageModal } from '../../../redux/globalSlices/genericSlice';
import FiservRefundModal from '../../modules/suppliers/FiservRefundModal/FiservRefundModal';
import { twMerge } from 'tailwind-merge';

export const AppPaidInvoicesModal = ({
  open,
  handleClose,
  reloadSelectedTransaction,
  accountType = 'supplier',
  selectedTransaction = null,
  onViewMainPayment,
}: {
  open: boolean;
  handleClose: (isReloadAllTransactionsOnClose?: boolean) => void;
  reloadSelectedTransaction?: () => void;
  accountType?: 'supplier' | 'buyer';
  selectedTransaction?: DillTransaction | null;
  onViewMainPayment: (val: string) => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedMainBuyer, selectedBuyerSuppliers } = useAppSelector((state) => state.buyers);
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [showMenu, setShowMenu] = useState(false);
  const [openDiscountDropDown, setOpenDiscountDropDown] = useState(false);
  const [openCreditMemoDropDown, setOpenCreditMemoDropDown] = useState(false);
  const [expandedCreditMemos, setExpandedCreditMemos] = useState<Set<number>>(new Set());
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [IsOpenLinkedTransactionDetails, setIsOpenLinkedTransactionDetails] = useState(false);
  const [selectedLinkedTransaction, setSelectedLinkedTransaction] =
    useState<DillTransaction | null>(null);
  const [isResendReceiptModalOpen, setIsResendReceiptModalOpen] = useState(false);
  const [isFiservRefundModalOpen, setIsFiservRefundModalOpen] = useState(false);
  const deviceWidth = useWindowActualWidth();
  const smallDeviceWidth = 800;
  const [isReloadAllTransactionsOnClose, setIsReloadAllTransactionsOnClose] = useState(false);
  const handleCloseModal = () => {
    handleClose(isReloadAllTransactionsOnClose);
    setOpenDiscountDropDown(false);
    setOpenCreditMemoDropDown(false);
    setIsResendReceiptModalOpen(false);
  };

  useEffect(() => {
    if (open) {
      setIsReloadAllTransactionsOnClose(false);
    }
    return () => {};
  }, [open]);

  useEffect(() => {
    const handler = (e: any) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };

    window.addEventListener('click', handler);
    return () => {
      window.removeEventListener('click', handler);
    };
  }, []);
  const selectedInvoices = useMemo(() => {
    return selectedTransaction?.invoicePayments ?? [];
  }, [selectedTransaction]);
  const columns = useMemo(() => {
    let currentColumns = [];
    if (selectedTransaction?.type === 'WILL_CALL_PAYMENT') {
      currentColumns = [
        { id: 'notes', name: 'Notes', width: 150, cellType: 'VIEW_TEXT' },
        { id: 'amountPaid', name: 'Payment', width: 120, cellType: 'VIEW_TEXT' },
      ];
    } else if (selectedTransaction?.type === 'PAYMENT_REQUEST_PAYMENT') {
      currentColumns = [
        { id: 'openAmount', name: 'Open Amount', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'amountPaid', name: 'Payment', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'notes', name: 'Notes', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'attachments', name: 'Attachments', width: 120, cellType: 'VIEW_TEXT' },
      ];
    } else if (selectedTransaction?.type === 'FIXED_AMOUNT_PAYMENT') {
      currentColumns = [
        { id: 'notes', name: 'Remittance Notes', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'amountPaid', name: 'Payment', width: 120, cellType: 'VIEW_TEXT' },
      ];
    } else {
      if (deviceWidth <= smallDeviceWidth) {
        currentColumns = [
          { id: 'invoiceNumber', name: 'Invoice #', width: 150, cellType: 'VIEW_TEXT' },
          { id: 'openAmount', name: 'Open Amount', width: 150, cellType: 'VIEW_TEXT' },
          { id: 'amountPaid', name: 'Payment', width: 150, cellType: 'VIEW_TEXT' },
          { id: 'actions', name: '', width: 50, cellType: 'VIEW_TEXT' },
        ];
      } else {
        currentColumns = [
          { id: 'invoiceNumber', name: 'Invoice #', width: 150, cellType: 'VIEW_TEXT' },
          { id: 'job', name: 'Job', width: 180, cellType: 'VIEW_TEXT' },
          { id: 'dueDate', name: 'Due Date', width: 180, cellType: 'VIEW_TEXT' },
          { id: 'openAmount', name: 'Open Amount', width: 150, cellType: 'VIEW_TEXT' },
          { id: 'amountPaid', name: 'Payment', width: 150, cellType: 'VIEW_TEXT' },
          { id: 'actions', name: '', width: 50, cellType: 'VIEW_TEXT' },
        ];
      }
    }
    return currentColumns;
  }, [selectedInvoices, selectedTransaction, deviceWidth]);

  const totalWidth = useMemo(
    () =>
      columns.reduce((curr, prev) => {
        return curr + prev.width;
      }, 0),
    [columns]
  );
  const error = useMemo(() => {
    if (selectedTransaction?.transactionErrorMessage) {
      const errorResult = selectedTransaction?.transactionErrorMessage.split('__');
      return errorResult;
    }
    return [];
  }, [selectedTransaction?.transactionErrorMessage]);

  const feeInfo = useMemo(() => {
    const type = selectedTransaction?.type;
    const isMonthlyFee = type === 'WITHDRAW' && !!selectedTransaction?.isFeeTakenMonthly;
    const isFiservAdjustmentFee = type === 'WITHDRAW' && !!selectedTransaction?.isFiservAdjustment;
    const isFee =
      type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
      type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER' ||
      type === 'FISERV_FEE_PAYMENT_BUYER' ||
      type === 'DWOLLA_FEE_PAYMENT_SUPPLIER' ||
      isMonthlyFee ||
      isFiservAdjustmentFee;

    let label = 'Fee';
    let description = 'This transaction is a fee.';
    if (isMonthlyFee) {
      label = 'Dill Monthly Bank ACH Processing Fees';
      description = 'Dill bank processing fees collected on a monthly basis';
    } else if (isFiservAdjustmentFee) {
      label = 'Dill Monthly Card Processing Fees';
      description = 'Dill card processing fees collected on a monthly basis';
    } else if (type === 'DWOLLA_FEE_PAYMENT_SUPPLIER') {
      label = 'Supplier Transaction Fee';
      description = 'A processing fee charged on a supplier transaction.';
    } else if (type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER') {
      label = 'Supplier Facilitation Fee';
      description = 'A Dill facilitation fee deducted from the supplier.';
    } else if (type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER') {
      label = 'Buyer Facilitation Fee';
      description = 'A Dill facilitation fee charged to the buyer.';
    } else if (type === 'FISERV_FEE_PAYMENT_BUYER') {
      label = 'Buyer Processing Fee';
      description = 'A card processing fee charged to the buyer.';
    }
    return { isFee, isMonthlyFee, label, description };
  }, [selectedTransaction]);

  // Once Dwolla reports the transfer settled we have the real arrival date, so only fall back to an
  // estimate while it is still in flight. Withdrawals that never landed get no arrival at all.
  const withdrawArrival = useMemo(() => {
    const status = selectedTransaction?.transactionStatus;
    if (status === 'failed' || status === 'cancelled') {
      return { label: '', date: '', description: '' };
    }
    if (selectedTransaction?.processedDate) {
      return {
        label: 'Arrived',
        date: moment(selectedTransaction.processedDate).format('MMM Do, YYYY'),
        description: '',
      };
    }
    const estimate = getEstimatedWithdrawArrival({
      initiatedAt: selectedTransaction?.createdAt,
      processingType: selectedTransaction?.withdrawProcessingType,
    });
    return {
      label: 'Estimated Arrival',
      date: estimate.arrivalDate ? moment(estimate.arrivalDate).format('MMM Do, YYYY') : '',
      description: estimate.description,
    };
  }, [selectedTransaction]);

  const selectedSupplier = useMemo(() => {
    const newSupplier: Supplier | null = null;
    if (selectedMainBuyer?.syncSupplierId && accountType === 'buyer') {
      const supplierId = selectedMainBuyer?.syncSupplierId;

      const supplier: Supplier | undefined = selectedBuyerSuppliers.find(
        (item: Supplier) => item.id === supplierId
      );
      return supplier;
    }
    if (currentSupplier && accountType === 'supplier') {
      return currentSupplier;
    }
    return newSupplier;
  }, [selectedBuyerSuppliers, open, selectedMainBuyer, user]);

  const fees = useMemo(() => {
    const total = selectedInvoices.reduce((accumulator: number, currentValue: any) => {
      return accumulator + currentValue?.amountPaid || 0;
    }, 0);
    return Number(selectedTransaction?.totalAmountPaid) - Number(total);
  }, [selectedInvoices, selectedTransaction?.totalAmountPaid]);
  const creditAmount = useMemo(() => {
    if (
      selectedTransaction &&
      selectedTransaction.linkedCreditsTransactions &&
      selectedTransaction?.linkedCreditsTransactions?.length > 0
    ) {
      const total = selectedTransaction?.linkedCreditsTransactions.reduce(
        (accumulator: number, currentValue: any) => {
          return accumulator + currentValue?.totalAmountPaid || 0;
        },
        0
      );
      return total;
    }
    return 0;
  }, [selectedTransaction]);
  // console.log({ selectedTransaction });

  const invoices = useMemo(() => {
    if (selectedTransaction && selectedTransaction.invoicePayments) {
      let result = [] as any;
      if (
        selectedTransaction.linkedCreditsTransactions &&
        selectedTransaction?.linkedCreditsTransactions?.length > 0
      ) {
        for (let i = 0; i < selectedTransaction?.linkedCreditsTransactions.length; i++) {
          const linkedInvoices = selectedTransaction?.linkedCreditsTransactions[i].invoicePayments;
          const combinedArray = linkedInvoices.map((item, index) => {
            const matchingItem = selectedInvoices.find(
              (element) => element.quickbooksInvoiceId === item.quickbooksInvoiceId
            );
            if (matchingItem) {
              const amount = item.amountPaid + (matchingItem?.amountPaid || 0);
              const discountAmount =
                (item.discountAmount || 0) + (matchingItem?.discountAmount || 0);
              return { ...matchingItem, amountPaid: amount, discountAmount };
            }
            return item;
          });
          result = result.concat(combinedArray);
        }
      }
      const resultIds = result.map((res: any) => res?.quickbooksInvoiceId);
      const invoicePayment = selectedTransaction.invoicePayments.filter((inv) => {
        return !resultIds.includes(inv?.quickbooksInvoiceId);
      });
      return invoicePayment.concat(result);
    }
    return [] as InvoicePaymentDetails[];
  }, [selectedTransaction, open]);

  const filteredInvoice = useMemo(() => {
    return invoices?.filter((item: any) => !item?.recordType || item.recordType === 'INVOICE');
  }, [invoices]);

  const fullCreditPay = useMemo(() => {
    if (selectedTransaction?.type === 'CREDIT_PAYMENT') {
      const total = selectedInvoices.reduce((accumulator: number, currentValue: any) => {
        return accumulator + currentValue?.amountPaid || 0;
      }, 0);
      return total;
    }
    return 0;
  }, [selectedInvoices, selectedTransaction, open]);

  const discount = useMemo(() => {
    if (selectedTransaction?.type === 'PAYMENT' || selectedTransaction?.type === 'CREDIT_PAYMENT') {
      const total = invoices.reduce((accumulator: number, currentValue: any) => {
        return accumulator + currentValue?.discountAmount || 0;
      }, 0);
      return total;
    }
    return 0;
  }, [selectedInvoices, selectedTransaction, open, invoices]);

  const supplierFees = useMemo(() => {
    if (
      selectedTransaction?.linkedFacilitationFeeTransactions &&
      selectedTransaction?.linkedFacilitationFeeTransactions.length > 0
    ) {
      const filterFeeTransaction = selectedTransaction?.linkedFacilitationFeeTransactions.filter(
        (item) => item.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
      );
      const total = filterFeeTransaction.reduce((accumulator: number, currentValue: any) => {
        return accumulator + currentValue.totalAmountPaid;
      }, 0);
      return total;
    }
    return 0;
  }, [selectedTransaction, open]);
  const buyerFees = useMemo(() => {
    if (
      selectedTransaction?.linkedFacilitationFeeTransactions &&
      selectedTransaction?.linkedFacilitationFeeTransactions.length > 0
    ) {
      const filterFeeTransaction = selectedTransaction?.linkedFacilitationFeeTransactions.filter(
        (item) => item.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER'
      );
      const total = filterFeeTransaction.reduce((accumulator: number, currentValue: any) => {
        return accumulator + currentValue.totalAmountPaid;
      }, 0);
      return total;
    }
    return 0;
  }, [selectedTransaction, open]);

  const buyerFiservFees = useMemo(() => {
    if (
      selectedTransaction?.linkedFacilitationFeeTransactions &&
      selectedTransaction?.linkedFacilitationFeeTransactions.length > 0
    ) {
      const filterFeeTransaction = selectedTransaction?.linkedFacilitationFeeTransactions.filter(
        (item) => item.type === 'FISERV_FEE_PAYMENT_BUYER'
      );
      const total = filterFeeTransaction.reduce((accumulator: number, currentValue: any) => {
        return accumulator + currentValue.totalAmountPaid;
      }, 0);
      return total;
    }
    return 0;
  }, [selectedTransaction, open]);

  const handleShowDropdown = () => {
    setShowMenu(!showMenu);
  };
  const handleViewRelease = (val: string) => {
    const releasePath =
      accountType === 'supplier'
        ? `/lienReleases?releaseId=${val}`
        : `/buyerLienReleases/${selectedMainBuyerId}/overview?releaseId=${val}`;
    const viewReleaseTab = window.open(releasePath, '_blank');
    if (viewReleaseTab) {
      viewReleaseTab.focus();
    }
    setShowMenu(!showMenu);
  };

  const handleDownloadPdf = async (invoiceId: string) => {
    if (selectedMainBuyer?.syncSupplierId || currentSupplier?.id) {
      const res = await dispatch(
        getInvoicePdf({
          supplierId:
            accountType === 'supplier'
              ? currentSupplier?.id || ''
              : selectedMainBuyer?.syncSupplierId,
          invoiceId,
        })
      );
      if (res && res.type === 'buyers/getInvoicePdf/fulfilled') {
        openDillToast({ message: 'invoice pdf successfully downloaded', type: 'SUCCESS' });
      } else {
        openDillToast({ message: 'Failed to download invoice pdf', type: 'ERROR' });
      }
    } else {
      openDillToast({ message: 'Failed to get supplier details', type: 'ERROR' });
    }
  };

  const handleDownloadAllInvoices = async () => {
    try {
      if (
        filteredInvoice.length > 0 &&
        (selectedMainBuyer?.syncSupplierId || currentSupplier?.id)
      ) {
        if (filteredInvoice.length === 1) {
          const res = await dispatch(
            getInvoicePdf({
              supplierId:
                accountType === 'supplier'
                  ? currentSupplier?.id || ''
                  : selectedMainBuyer?.syncSupplierId,
              invoiceId:
                filteredInvoice[0]?.quickbooksInvoiceDetails?.id ??
                filteredInvoice[0]?.providerInvoiceId ??
                '',
            })
          );
          if (res && res.type === 'buyers/getInvoicePdf/fulfilled') {
            openDillToast({ message: 'invoice pdf successfully downloaded', type: 'SUCCESS' });
          } else {
            openDillToast({ message: 'Failed to download invoice pdf', type: 'ERROR' });
          }
        } else {
          const res = await dispatch(
            downloadMultiInvoicePdfZip({
              supplierId:
                accountType === 'supplier'
                  ? currentSupplier?.id || ''
                  : selectedMainBuyer?.syncSupplierId,
              invoiceIds: filteredInvoice.map((invoice: any) => {
                return invoice?.quickbooksInvoiceDetails?.id ?? invoice?.providerInvoiceId;
              }),
            })
          );
          if (res && res.type === 'buyers/downloadMultiInvoicePdfZip/fulfilled') {
            openDillToast({
              message: 'All invoice pdf zip successfully downloaded',
              type: 'SUCCESS',
            });
          } else {
            openDillToast({ message: 'Failed to download all invoice pdf zip', type: 'ERROR' });
          }
        }
      }
    } catch (err) {
      openDillToast({ message: 'Failed to download all invoice pdf zip', type: 'ERROR' });
      console.log(err);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      const res = await dispatch(
        downloadInvoicePdfReceipt({
          supplierId:
            accountType === 'supplier'
              ? currentSupplier?.id || ''
              : selectedMainBuyer?.syncSupplierId,
          transactionId: selectedTransaction?.id || '',
        })
      );
      if (res && res.type === 'buyers/downloadInvoicePdfReceipt/fulfilled') {
        openDillToast({
          message: 'Successfully downloaded receipt',
          type: 'SUCCESS',
        });
      } else {
        openDillToast({ message: 'Failed to download receipt', type: 'ERROR' });
      }
    } catch (err: any) {
      openDillToast({ message: err.message || 'Failed to download receipt', type: 'ERROR' });
      console.log(err);
    }
  };

  const discountData = useMemo(() => {
    const discountTable = [];
    for (let i = 0; i < invoices.length; i++) {
      const invoice = invoices[i];
      if (invoice.discountAmount && invoice.discountAmount > 0) {
        const amountPaid = invoice?.amountPaid ?? 0;
        const discountAmount = invoice?.discountAmount ?? 0;
        const totalAmountPaid = amountPaid + discountAmount;
        discountTable.push({
          dueDate: invoice?.dueDate ?? '',
          invoiceNumber: invoice?.invoiceNumber ?? '',
          discountAmount: invoice?.discountAmount ?? '',
          amount: totalAmountPaid,
          terms: (invoice as any)?.quickbooksInvoiceDetails?.terms ?? '',
        });
      }
    }
    return discountTable;
  }, [invoices]);

  const creditDiscount = useMemo(() => {
    if (selectedTransaction?.type === 'PAYMENT' && selectedTransaction?.creditDiscountUsed) {
      return selectedTransaction.creditDiscountUsed.reduce(
        (acc, curr) => acc + (curr.creditAmount ?? 0),
        0
      );
    }
    return 0;
  }, [selectedTransaction]);

  const creditMemosGroup = useMemo(() => {
    let creditMemosTable: {
      docNumber: string;
      id: string;
      jobId: string;
      amount: number;
      invoicePayments: {
        invoiceNumber: string;
        amountPaid: number;
        creditUsedId: string;
        amountPaidForThisCredit: number;
      }[];
    }[] = [];
    if (selectedTransaction && selectedTransaction?.linkedCreditsTransactions) {
      for (let i = 0; i < selectedTransaction?.linkedCreditsTransactions.length; i++) {
        const transaction = selectedTransaction?.linkedCreditsTransactions[i];
        if (!transaction.creditsUsed || transaction.creditsUsed.length === 0) {
          continue;
        }

        const groupedCreditsUsed = (transaction?.creditsUsed || []).reduce(
          (
            acc: {
              [x: string]: {
                docNumber: string;
                id: string;
                jobId: string;
                amount: number;
                invoicePayments: {
                  invoiceNumber: string;
                  amountPaid: number;
                  creditUsedId: string;
                  amountPaidForThisCredit: number;
                }[];
              };
            },
            item: DillCreditUsed
          ) => {
            const id = item?.docNumber || item?.platformCreditMemoId || '';
            const existingEntry = acc[id];
            const invoicePayments = transaction?.invoicePayments.filter(
              (invoicePayment) => item.appliesTo === invoicePayment.invoiceNumber
            );
            if (existingEntry) {
              acc[id] = {
                ...acc[id],
                amount: acc[id].amount + item.amount - (item?.discountAmount ?? 0),
                invoicePayments: [
                  ...acc[id].invoicePayments,
                  ...invoicePayments.map((inv) => {
                    return {
                      creditUsedId: id,
                      amountPaid: inv.amountPaid - (inv?.discountAmount ?? 0),
                      invoiceNumber: inv.invoiceNumber,
                      amountPaidForThisCredit: item.amount - (item?.discountAmount ?? 0),
                    };
                  }),
                ],
              };
            } else {
              acc[id] = {
                amount: item.amount - (item?.discountAmount ?? 0),
                docNumber: item.docNumber || item.platformCreditMemoId || '',
                id: id,
                jobId: item.jobId || '',
                invoicePayments: [
                  ...invoicePayments.map((inv) => {
                    return {
                      creditUsedId: id,
                      amountPaid: inv.amountPaid - (inv?.discountAmount ?? 0),
                      invoiceNumber: inv.invoiceNumber,
                      amountPaidForThisCredit: item.amount - (item?.discountAmount ?? 0),
                    };
                  }),
                ],
              };
            }
            return acc;
          },
          {}
        );

        creditMemosTable = Object.values(groupedCreditsUsed);
      }
    }
    return creditMemosTable;
  }, [selectedTransaction]);

  const creditDiscountData = useMemo(() => {
    if (selectedTransaction?.type !== 'PAYMENT' || !selectedTransaction?.creditDiscountUsed) {
      return [];
    }
    return selectedTransaction.creditDiscountUsed.map(
      ({ creditId, creditAmount: creditDiscountAmount }) => {
        const creditMemo = creditMemosGroup.find((c) => c.id === creditId);
        const creditNo = creditMemo?.docNumber || '';
        const balance = creditMemo?.amount ?? 0;
        let discountDate = '';
        let terms = '';

        const linkedTx = selectedTransaction.linkedCreditsTransactions?.find((tx) =>
          tx.creditsUsed?.some((c) => c.id === creditId)
        );
        if (linkedTx) {
          discountDate = '';
          const credit = linkedTx.creditsUsed?.find((c) => c.id === creditId);
          const matchingInvoice = invoices.find((inv) => inv.invoiceNumber === credit?.appliesTo);
          terms = (matchingInvoice as any)?.quickbooksInvoiceDetails?.terms ?? '';
        }

        return { creditNo, discountDate, balance, terms, discountAmount: creditDiscountAmount };
      }
    );
  }, [selectedTransaction, invoices, creditMemosGroup]);
  const creditMemos = useMemo(() => {
    const creditMemosTable = [];
    if (selectedTransaction && selectedTransaction?.linkedCreditsTransactions) {
      for (let i = 0; i < selectedTransaction?.linkedCreditsTransactions.length; i++) {
        const transaction = selectedTransaction?.linkedCreditsTransactions[i];
        if (!transaction.creditsUsed || transaction.creditsUsed.length === 0) {
          continue;
        }
        for (let j = 0; j < transaction?.creditsUsed.length; j++) {
          const invoice = transaction?.creditsUsed[j];
          const amountPaid = invoice?.amount ?? 0;
          const discountAmount = invoice?.discountAmount ?? 0;
          const totalAmountPaid = amountPaid - discountAmount;
          creditMemosTable.push({
            id: invoice?.id ?? '',
            docNumber: invoice?.docNumber ?? '',
            invoice: invoice?.appliesTo ?? '',
            balance: totalAmountPaid,
            recordType: invoice?.recordType ?? '',
          });
        }
      }
    }
    return creditMemosTable;
  }, [selectedTransaction]);

  const handleViewLinkedPayment = (val: string) => {
    handleCloseModal();
    onViewMainPayment(val);
  };

  const totalFiservAdjustments = useMemo(() => {
    let total = 0;
    if (
      accountType === 'supplier' &&
      currentSupplier?.cardPaymentProvider === 'FISERV' &&
      selectedTransaction &&
      selectedTransaction?.metaData?.adjustments &&
      selectedTransaction?.metaData?.adjustments.length > 0
    ) {
      total = [...selectedTransaction?.metaData?.adjustments].reduce((prev, curr) => {
        return prev + Math.abs(Number(curr?.amount || 0));
      }, 0);
    }
    return total;
  }, [selectedTransaction]);

  const totalFiservRefunds = useMemo(() => {
    let total = 0;
    if (
      accountType === 'supplier' &&
      currentSupplier?.cardPaymentProvider === 'FISERV' &&
      selectedTransaction &&
      selectedTransaction?.metaData?.fiservRefundTransactions &&
      selectedTransaction?.metaData?.fiservRefundTransactions.length > 0
    ) {
      total = [...selectedTransaction?.metaData?.fiservRefundTransactions].reduce((prev, curr) => {
        return prev + Math.abs(Number(curr?.amount || 0));
      }, 0);
    }
    return total;
  }, [selectedTransaction]);

  const renderDiscountDropDown = () => {
    const netDiscount = discount - creditDiscount;
    const discountText =
      netDiscount > 0
        ? `-${formatMoney(Math.abs(netDiscount), 2)}`
        : `+${formatMoney(Math.abs(netDiscount), 2)}`;
    return (
      <div className="PRIMARY_300-BORDER border mb-2 rounded overflow-hidden">
        <div
          className="flex flex-row justify-between  cursor-pointer p-1 hover:bg-indigo-100"
          onClick={() => setOpenDiscountDropDown(!openDiscountDropDown)}>
          <div className="flex items-center justify-center">
            {openDiscountDropDown ? (
              <CaretUp color={COLORS.GREY_500} size={19} />
            ) : (
              <CaretDown color={COLORS.GREY_500} size={19} />
            )}
            <div className="amount-label-text ml-2">Discount</div>
          </div>
          <div className="amount-text">{discountText}</div>
        </div>
        {openDiscountDropDown && (
          <div className="WHITE-BG p-2">
            {discountData.length > 0 && (
              <div className="w-full px-[4px] py-1 flex justify-between PRIMARY_100-BG">
                <div className="text-xs font-bold">Invoice No</div>
                <div className="flex">
                  <div className="text-xs w-[100px] font-bold">Due Date</div>
                  <div className="text-xs w-[80px] font-bold">Amount</div>
                  <div className="text-xs w-[150px] font-bold">Terms</div>
                  <div className="text-xs w-[80px] flex justify-end font-bold">Discount</div>
                </div>
              </div>
            )}
            {discountData.map((invoice) => {
              return (
                <div
                  className="w-full px-[2px] py-1 flex justify-between"
                  key={`dropdown_discount_${invoice.invoiceNumber}`}>
                  <div className="text-xs">{invoice.invoiceNumber}</div>
                  <div className="flex">
                    <div className="text-xs w-[100px]">
                      {moment(invoice.dueDate).utc().format('MM/DD/YYYY')}
                    </div>
                    <div className="text-xs w-[80px]">{`${formatMoney(
                      invoice?.amount ?? '',
                      2
                    )}`}</div>
                    <div className="text-xs w-[150px]">{invoice.terms}</div>
                    <div className="text-xs w-[80px] flex justify-end">{`-${formatMoney(
                      invoice?.discountAmount ?? '',
                      2
                    )}`}</div>
                  </div>
                </div>
              );
            })}
            {creditDiscountData.length > 0 && (
              <>
                <div className="w-full px-[4px] py-1 flex justify-between PRIMARY_100-BG mt-1">
                  <div className="text-xs font-bold">Credit No</div>
                  <div className="flex">
                    <div className="text-xs w-[100px] font-bold">Due Date</div>
                    <div className="text-xs w-[80px] font-bold">Amount</div>
                    <div className="text-xs w-[150px] font-bold">Terms</div>
                    <div className="text-xs w-[80px] flex justify-end font-bold">Discount</div>
                  </div>
                </div>
                {creditDiscountData.map((credit, i) => (
                  <div
                    className="w-full px-[2px] py-1 flex justify-between"
                    key={`dropdown_credit_discount_${i}`}>
                    <div className="text-xs">{credit.creditNo}</div>
                    <div className="flex">
                      <div className="text-xs w-[100px]">{credit.discountDate}</div>
                      <div className="text-xs w-[80px]">{formatMoney(credit.balance, 2)}</div>
                      <div className="text-xs w-[150px]">{credit.terms}</div>
                      <div className="text-xs w-[80px] flex justify-end">{`+${formatMoney(
                        credit.discountAmount,
                        2
                      )}`}</div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCreditDropDown = () => {
    return (
      <div className="PRIMARY_300-BORDER border mb-2 rounded overflow-hidden">
        <div
          className="flex flex-row justify-between  cursor-pointer p-1 hover:bg-indigo-100"
          onClick={() => setOpenCreditMemoDropDown(!openCreditMemoDropDown)}>
          <div className="flex items-center justify-center">
            {openCreditMemoDropDown ? (
              <CaretUp color={COLORS.GREY_500} size={19} />
            ) : (
              <CaretDown color={COLORS.GREY_500} size={19} />
            )}
            <div className="amount-label-text ml-2">Credits</div>
          </div>
          <div className="amount-text">{`-${formatMoney(creditAmount, 2)}`}</div>
        </div>
        {openCreditMemoDropDown && (
          <div className="WHITE-BG p-2">
            <div className="w-full px-[4px] py-1 flex justify-between PRIMARY_100-BG">
              <div className="flex">
                <div className="text-xs font-bold w-[120px]">Doc No#</div>
                <div className="text-xs font-bold w-[120px]">Invoice Applied</div>
              </div>
              <div className="flex">
                <div className="text-xs w-[100px] flex justify-end font-bold">Amount</div>
              </div>
            </div>
            {creditMemosGroup.map((creditMemo, i) => {
              const { invoicePayments } = creditMemo;
              const isExpanded = expandedCreditMemos.has(i);
              const hasInvoicePayments = invoicePayments && invoicePayments.length > 0;

              return (
                <div
                  key={`credit_memo_dropdown_oo_${i}`}
                  className="w-full px-[2px] py-1 flex flex-col">
                  <div
                    className={twMerge(
                      `flex justify-between `,
                      hasInvoicePayments ? 'cursor-pointer hover:bg-slate-100' : '',
                      isExpanded ? 'bg-blue-200' : ''
                    )}
                    onClick={() => {
                      if (hasInvoicePayments) {
                        const newExpanded = new Set(expandedCreditMemos);
                        if (newExpanded.has(i)) {
                          newExpanded.delete(i);
                        } else {
                          newExpanded.add(i);
                        }
                        setExpandedCreditMemos(newExpanded);
                      }
                    }}>
                    <div className="flex items-center">
                      {hasInvoicePayments && (
                        <div className="mr-1">
                          {isExpanded ? (
                            <CaretUp color={COLORS.GREY_500} size={14} />
                          ) : (
                            <CaretDown color={COLORS.GREY_500} size={14} />
                          )}
                        </div>
                      )}
                      <div className="flex">
                        <div className="text-xs w-[120px]">{creditMemo.docNumber}</div>
                        <div className="text-xs w-[120px]">{}</div>
                      </div>
                    </div>
                    <div className="flex">
                      <div className="text-xs w-[100px] flex justify-end">{`-${formatMoney(
                        creditMemo.amount ?? '',
                        2
                      )}`}</div>
                    </div>
                  </div>
                  {isExpanded && hasInvoicePayments && (
                    <div className="flex flex-col bg-slate-50">
                      {invoicePayments.map((invoicePayment, ip) => {
                        return (
                          <div key={`ip-${ip}`} className="flex justify-between">
                            <div className="flex">
                              <div className="text-xs w-[120px]">{}</div>
                              <div className="text-xs w-[120px]">
                                {invoicePayment.invoiceNumber}
                              </div>
                            </div>
                            <div className="flex">
                              <div className="text-xs w-[100px] flex justify-end">{`-${formatMoney(
                                invoicePayment.amountPaidForThisCredit ?? '',
                                2
                              )}`}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <div className="invoice-select-pay-modal-content pt-3">
        <AppPaidInvoicesModal
          open={IsOpenLinkedTransactionDetails}
          handleClose={() => {
            setIsOpenLinkedTransactionDetails(false);
          }}
          accountType="supplier"
          selectedTransaction={selectedLinkedTransaction}
          onViewMainPayment={() => {}}
        />
        <ResendReceiptModal
          open={isResendReceiptModalOpen}
          selectedTransaction={selectedTransaction}
          handleClose={() => setIsResendReceiptModalOpen(false)}
        />
        <FiservRefundModal
          open={isFiservRefundModalOpen}
          selectedTransaction={selectedTransaction}
          handleClose={(isRefunded) => {
            if (isRefunded) {
              if (reloadSelectedTransaction) {
                reloadSelectedTransaction();
              }
              setIsReloadAllTransactionsOnClose(true);
            }
            setIsFiservRefundModalOpen(false);
          }}
        />
        <div>
          <div className="payment-modal-title-container">
            {selectedTransaction?.type === 'PAYMENT' ||
            selectedTransaction?.type === 'WILL_CALL_PAYMENT' ||
            selectedTransaction?.type === 'PAYMENT_REQUEST_PAYMENT' ||
            selectedTransaction?.type === 'CREDIT_PAYMENT' ||
            selectedTransaction?.type === 'FIXED_AMOUNT_PAYMENT' ? (
              <h3 className="paid-invoice-modal-title">
                {`${invoices.length > 1 ? 'Bulk Payment' : 'Payment'}`}
              </h3>
            ) : feeInfo.isFee ? (
              <h3 className="paid-invoice-modal-title flex items-center gap-2">
                {feeInfo.label}
                <span className="rounded-full bg-amber-500 px-2 py-0.5 text-2xs font-bold uppercase tracking-wide text-white">
                  Fee
                </span>
              </h3>
            ) : (
              <h3 className="paid-invoice-modal-title">
                {`${selectedTransaction?.type === 'WITHDRAW' ? 'Withdrawal Details' : 'Fee'}`}
              </h3>
            )}
            <AppButton
              type="FLAT"
              buttonStyles={{ padding: '0px', margin: '5px 2px' }}
              icon={<X color={COLORS.GREY_300} size={30} />}
              onClick={handleCloseModal}
            />
          </div>
          <div className="paid-invoice-modal-list-content">
            {feeInfo.isFee && (
              <div className="mx-4 mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                    <ArrowURightDown color={COLORS.WARNING_500} size={20} weight="bold" />
                  </div>
                  <div className="flex flex-col">
                    <p className="text-sm font-bold text-amber-800">{feeInfo.label}</p>
                    <p className="text-xs text-amber-700">{feeInfo.description}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-2xs font-semibold uppercase tracking-wide text-amber-600">
                      Fee Amount
                    </p>
                    <p className="text-base font-bold text-amber-800">
                      {selectedTransaction
                        ? formatMoney(selectedTransaction?.totalAmountPaid, 2)
                        : ''}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {selectedTransaction?.type === 'WITHDRAW' && (
              <div>
                <div className="flex flex-row align-center justify-between">
                  <div className="flex flex-col mx-6 mt-4">
                    <p className="text-base font-bold">
                      {selectedTransaction?.paymentMethod?.type === 'BANK_ACCOUNT'
                        ? 'Bank Account'
                        : 'Credit Card'}
                    </p>
                    <p className="bank-detail-number">{`${
                      selectedTransaction?.recipient?.name
                        ? selectedTransaction?.recipient?.name.toLowerCase()
                        : ''
                    } | ${selectedTransaction?.paymentMethod?.paymentMethodName}`}</p>
                  </div>
                  <div className="self-center mx-6 mt-4">
                    <AppTransactionStatusPill status={selectedTransaction?.transactionStatus} />
                  </div>
                </div>
                <div className="flex flex-col w-full  mb-3 px-4">
                  <div className="flex flex-col justify-between  mt-4 rounded-xl PRIMARY_50-BG px-5 py-4">
                    <div className="">
                      <div className="flex flex-row justify-between w-full pb-2 ">
                        <div className="withdrawal-date">Withdrawal Date</div>
                        <div className="text-sm font-bold">
                          {moment(selectedTransaction?.createdAt).format('MMM Do, YYYY h:mm A')}
                        </div>
                      </div>
                      {withdrawArrival.date && (
                        <div className="flex flex-row justify-between w-full pb-2">
                          <div className="withdrawal-date">{withdrawArrival.label}</div>
                          <div className="flex flex-col items-end">
                            <div className="text-sm font-bold">{withdrawArrival.date}</div>
                            {withdrawArrival.description && (
                              <div className="text-xs TEXT_SECONDARY-CLR">
                                {withdrawArrival.description}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {selectedTransaction &&
                      selectedTransaction?.linkedWithdrawTransactions &&
                      selectedTransaction?.linkedWithdrawTransactions.length > 0 && (
                        <div className="bg-white rounded-lg px-4 py-3">
                          <p className="text-xs text-gray-500 mb-3 font-semibold">
                            INCLUDED PAYMENTS
                          </p>
                          <div className="">
                            {selectedTransaction &&
                              selectedTransaction?.linkedWithdrawTransactions &&
                              selectedTransaction?.linkedWithdrawTransactions.length > 0 &&
                              selectedTransaction?.linkedWithdrawTransactions?.map(
                                (transaction, i) => {
                                  let senderName = selectedTransaction?.recipient?.name ?? '';
                                  if (
                                    transaction.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER' ||
                                    transaction.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
                                  ) {
                                    senderName = 'Dill';
                                  } else if (
                                    transaction.type === 'PAYMENT' ||
                                    transaction.type === 'PAYMENT_REQUEST_PAYMENT' ||
                                    transaction.type === 'FIXED_AMOUNT_PAYMENT'
                                  ) {
                                    senderName = `${transaction?.buyer?.name} - ${
                                      transaction?.buyer?.quickbooksCustomerId || ''
                                    }`.toLowerCase();
                                  } else if (
                                    transaction.type === 'WILL_CALL_PAYMENT' &&
                                    transaction?.buyer
                                  ) {
                                    senderName = `${transaction?.buyer?.name} - ${
                                      transaction?.buyer?.quickbooksCustomerId || ''
                                    }`.toLowerCase();
                                  }
                                  const transactionType = transaction.type;
                                  let totalBuyerFees = 0;
                                  let totalSupplierFees = 0;
                                  if (transaction?.linkedFacilitationFeeTransactions) {
                                    const filterBuyerFeeTransaction =
                                      transaction?.linkedFacilitationFeeTransactions.filter(
                                        (item) =>
                                          item.type === 'DILL_FACILITATION_FEE_PAYMENT_BUYER'
                                      );
                                    totalBuyerFees = filterBuyerFeeTransaction.reduce(
                                      (accumulator: number, currentValue: any) => {
                                        return accumulator + currentValue.totalAmountPaid;
                                      },
                                      0
                                    );
                                    const filterSupplierFeeTransaction =
                                      transaction?.linkedFacilitationFeeTransactions.filter(
                                        (item) =>
                                          item.type === 'DILL_FACILITATION_FEE_PAYMENT_SUPPLIER'
                                      );
                                    totalSupplierFees = filterSupplierFeeTransaction.reduce(
                                      (accumulator: number, currentValue: any) => {
                                        return accumulator + currentValue.totalAmountPaid;
                                      },
                                      0
                                    );
                                  }

                                  return (
                                    <a
                                      key={`${i}-a`}
                                      target="_blank"
                                      className="flex justify-between mt-2 py-1 cursor-pointer hover:bg-slate-50 rounded-lg"
                                      onClick={() => {
                                        if (
                                          transactionType === 'DWOLLA_FEE_PAYMENT_SUPPLIER' &&
                                          transaction.linkedDillTransactionId
                                        ) {
                                          (async () => {
                                            const transactionResults = await dispatch(
                                              getTransactionById({
                                                transactionId:
                                                  transaction.linkedDillTransactionId || '',
                                              })
                                            );
                                            let finalTransaction = null;
                                            if (
                                              transactionResults.type ===
                                              'payments/getTransactionById/fulfilled'
                                            ) {
                                              const res = transactionResults?.payload as any;
                                              finalTransaction = res.data as DillTransaction;
                                            }
                                            if (finalTransaction) {
                                              setSelectedLinkedTransaction(finalTransaction);
                                              setIsOpenLinkedTransactionDetails(true);
                                            }
                                          })();
                                        } else {
                                          setSelectedLinkedTransaction(transaction);
                                          setIsOpenLinkedTransactionDetails(true);
                                        }
                                      }}>
                                      <div className=" w-full " style={{ flex: 2 / 3 }}>
                                        <div className="w-full flex items-center">
                                          <div className="letter-container">
                                            {getFirstLetters(senderName || '')}
                                          </div>
                                          <div>
                                            <div className="name-text">{senderName || ''}</div>
                                            <div className="flex ">
                                              <div>
                                                <div className="flex">
                                                  {transactionType === 'PAYMENT' ||
                                                  transactionType === 'WILL_CALL_PAYMENT' ||
                                                  transactionType === 'PAYMENT_REQUEST_PAYMENT' ||
                                                  transactionType === 'CREDIT_PAYMENT' ||
                                                  transactionType === 'FIXED_AMOUNT_PAYMENT' ? (
                                                    <ArrowDownLeft
                                                      color={COLORS.SUCCESS_500}
                                                      size={10}
                                                    />
                                                  ) : (
                                                    <ArrowDownLeft
                                                      color={COLORS.ERROR_500}
                                                      size={10}
                                                      className="rotate-180"
                                                    />
                                                  )}
                                                  <div className="action-type-text">
                                                    {`${
                                                      transactionType === 'PAYMENT' ||
                                                      transactionType === 'CREDIT_PAYMENT' ||
                                                      transactionType === 'WILL_CALL_PAYMENT' ||
                                                      transactionType ===
                                                        'PAYMENT_REQUEST_PAYMENT' ||
                                                      transactionType === 'FIXED_AMOUNT_PAYMENT'
                                                        ? 'RECEIVED'
                                                        : transactionType ===
                                                          'DWOLLA_FEE_PAYMENT_SUPPLIER'
                                                        ? 'Supplier transaction fees'
                                                        : transaction.type.replaceAll('_', ' ')
                                                    }`}
                                                  </div>
                                                </div>
                                                {transactionType ===
                                                  'DWOLLA_FEE_PAYMENT_SUPPLIER' &&
                                                  transaction.linkedDillTransactionId && (
                                                    <div
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        (async () => {
                                                          const transactionResults = await dispatch(
                                                            getTransactionById({
                                                              transactionId:
                                                                transaction.linkedDillTransactionId ||
                                                                '',
                                                            })
                                                          );
                                                          let finalTransaction = null;
                                                          if (
                                                            transactionResults.type ===
                                                            'payments/getTransactionById/fulfilled'
                                                          ) {
                                                            const res =
                                                              transactionResults?.payload as any;
                                                            finalTransaction =
                                                              res.data as DillTransaction;
                                                          }
                                                          if (finalTransaction) {
                                                            setSelectedLinkedTransaction(
                                                              finalTransaction
                                                            );
                                                            setIsOpenLinkedTransactionDetails(true);
                                                          }
                                                        })();
                                                      }}
                                                      className="group mt-1 inline-flex w-fit items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-600 transition-colors cursor-pointer hover:border-blue-300 hover:bg-blue-100 hover:text-blue-700">
                                                      <ArrowSquareOut size={12} weight="bold" />
                                                      <span>View associated transaction</span>
                                                      <ArrowRight
                                                        size={12}
                                                        weight="bold"
                                                        className="transition-transform group-hover:translate-x-0.5"
                                                      />
                                                    </div>
                                                  )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="w-full" style={{ flex: 0.5 / 3 }}>
                                        <p
                                          className={`font-bold text-xs ${
                                            transaction.isFeeTakenMonthly ? 'text-amber-700' : ''
                                          }`}>
                                          {transaction.isFeeTakenMonthly ? '-' : '+'}
                                          {formatMoney(
                                            transaction.totalAmountPaid -
                                              totalBuyerFees -
                                              totalSupplierFees,
                                            2
                                          )}
                                        </p>
                                      </div>
                                      <div className="w-full" style={{ flex: 0.5 / 3 }}>
                                        <p className="text-xs">
                                          {transaction.createdAt
                                            ? moment(transaction.createdAt).format('MM/DD/YYYY')
                                            : ''}
                                        </p>
                                      </div>
                                    </a>
                                  );
                                }
                              )}
                          </div>
                        </div>
                      )}
                    {accountType === 'supplier' &&
                      currentSupplier?.cardPaymentProvider === 'FISERV' &&
                      selectedTransaction &&
                      selectedTransaction?.metaData?.adjustments &&
                      selectedTransaction?.metaData?.adjustments.length > 0 && (
                        <div className="bg-white rounded-lg px-4 py-3">
                          <p className="text-xs text-gray-500 mb-3 font-semibold">Adjustments</p>
                          <div className="">
                            {selectedTransaction &&
                              selectedTransaction?.metaData?.adjustments &&
                              selectedTransaction?.metaData?.adjustments.length > 0 &&
                              [...selectedTransaction?.metaData?.adjustments].map(
                                (adjustment, i) => {
                                  return (
                                    <div
                                      key={`${i}-a`}
                                      className="flex justify-between mt-2 py-1 cursor-pointer hover:bg-slate-50 rounded-lg">
                                      <div className="flex flex-col">
                                        <p className="capitalize text-sm font-medium">
                                          {(adjustment?.description || '').toLowerCase()}
                                        </p>
                                        <p className="capitalize text-xs">
                                          {(adjustment?.type || '').toLowerCase()}
                                        </p>
                                      </div>

                                      <p className="text-xs font-bold">
                                        {formatMoney(adjustment?.amount, 2) || ''}
                                      </p>
                                    </div>
                                  );
                                }
                              )}
                          </div>
                        </div>
                      )}
                    {accountType === 'supplier' &&
                      currentSupplier?.cardPaymentProvider === 'FISERV' &&
                      selectedTransaction &&
                      selectedTransaction?.metaData?.fiservRefundTransactions &&
                      selectedTransaction?.metaData?.fiservRefundTransactions.length > 0 && (
                        <div className="bg-white rounded-lg px-4 py-3">
                          <p className="text-xs text-gray-500 mb-3 font-semibold">Refunds</p>
                          <div className="">
                            {selectedTransaction &&
                              selectedTransaction?.metaData?.fiservRefundTransactions &&
                              selectedTransaction?.metaData?.fiservRefundTransactions.length > 0 &&
                              [...selectedTransaction?.metaData?.fiservRefundTransactions].map(
                                (refundDetails, i) => {
                                  return (
                                    <div
                                      key={`${i}-a`}
                                      className="flex justify-between mt-2 py-1 cursor-pointer hover:bg-slate-50 rounded-lg">
                                      <div className="flex flex-col">
                                        <p className="capitalize text-sm font-medium">
                                          {(refundDetails?.retref || '').toLowerCase()}
                                        </p>
                                        <p className="capitalize text-xs ">
                                          {(refundDetails?.cardnumber || '').toLowerCase()}
                                        </p>
                                      </div>

                                      <div className="flex flex-col items-end">
                                        <p className="text-xs font-bold">
                                          {formatMoney(
                                            refundDetails?.amount || refundDetails?.setlamount,
                                            2
                                          ) || ''}
                                        </p>
                                        {refundDetails.authdate && (
                                          <p className="text-2xs">
                                            {moment(
                                              refundDetails.authdate,
                                              'YYYYMMDDHHmmss'
                                            ).format('MMM DD, YYYY h:mm A') || ''}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                              )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}
            {(selectedTransaction?.type === 'PAYMENT' ||
              selectedTransaction?.type === 'WILL_CALL_PAYMENT' ||
              selectedTransaction?.type === 'PAYMENT_REQUEST_PAYMENT' ||
              selectedTransaction?.type === 'FIXED_AMOUNT_PAYMENT') && (
              <div>
                <div className="flex flex-col align-center justify-between">
                  <div className="flex  mx-4 mt-3 justify-between">
                    <div className="flex w-full flex-col my-1 mx-1">
                      <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">Date</p>
                      <p className="text-xs font-bold capitalize">
                        {moment(selectedTransaction.createdAt).format('MMM DD, YYYY h:mm A')}
                      </p>
                    </div>
                    <div className="flex w-full flex-col my-1 mx-1">
                      <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">To</p>
                      <p className="text-xs font-bold capitalize">
                        {selectedTransaction?.recipient?.name
                          ? selectedTransaction?.recipient?.name.toLowerCase()
                          : ''}
                      </p>
                    </div>
                    <div className="flex w-full flex-col my-1 mx-1">
                      <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">From</p>

                      <p className="text-xs font-bold capitalize">
                        {selectedTransaction?.buyer
                          ? `${selectedTransaction?.buyer?.name} - ${
                              selectedTransaction?.buyer?.quickbooksCustomerId || ''
                            }`.toLowerCase()
                          : ''}
                      </p>

                      {selectedTransaction?.type !== 'PAYMENT_REQUEST_PAYMENT' &&
                        selectedTransaction.type !== 'WILL_CALL_PAYMENT' &&
                        selectedTransaction?.sender?.user?.email && (
                          <div className="flex flex-col mt-1">
                            <p className="text-2xs font-bold lowercase">
                              {selectedTransaction?.sender?.user?.email.toLowerCase()}
                            </p>
                            {selectedTransaction?.initiatedBy && (
                              <>
                                <p className="mt-1 text-xs font-bold capitalize italic text-black">
                                  {selectedTransaction?.initiatedBy ? 'Initiated by' : ''}
                                </p>
                                <p className="text-2xs font-bold lowercase">
                                  {selectedTransaction?.initiatedBy
                                    ? `${selectedTransaction?.initiatedBy.email}`
                                    : ''}
                                </p>
                              </>
                            )}
                          </div>
                        )}
                      {selectedTransaction?.type === 'PAYMENT_REQUEST_PAYMENT' && (
                        <p className="text-2xs font-bold lowercase">
                          {selectedTransaction?.paymentRequest?.paymentConfirmationEmail || ''}
                        </p>
                      )}
                      {selectedTransaction?.type === 'WILL_CALL_PAYMENT' &&
                      selectedTransaction?.receiptEmails &&
                      selectedTransaction?.receiptEmails?.length > 0 ? (
                        <p className="text-2xs font-bold lowercase">
                          {selectedTransaction.receiptEmails[0] || ''}
                        </p>
                      ) : selectedTransaction?.sender?.user?.email &&
                        selectedTransaction?.type === 'WILL_CALL_PAYMENT' ? (
                        <p className="text-2xs font-bold lowercase">
                          {selectedTransaction?.sender?.user?.email.toLowerCase()}
                        </p>
                      ) : (
                        <></>
                      )}
                      {selectedTransaction.type === 'WILL_CALL_PAYMENT' && (
                        <div className="flex flex-col mt-1">
                          <p className="text-xs font-bold capitalize italic text-black">(POS)</p>

                          {selectedTransaction?.initiatedBy && (
                            <>
                              <p className="mt-1 text-xs font-bold capitalize italic text-black">
                                Initiated by
                              </p>
                              <p className="text-2xs font-bold lowercase">
                                {selectedTransaction?.initiatedBy.email}
                              </p>
                            </>
                          )}
                        </div>
                      )}
                      {selectedTransaction.type === 'PAYMENT_REQUEST_PAYMENT' && (
                        <div className="">
                          <p className="text-xs font-bold capitalize italic text-black">
                            (Payment Request Payment)
                          </p>
                          {selectedTransaction?.paymentRequest?.senderUserSummary && (
                            <>
                              <p className="mt-1 text-xs font-bold capitalize italic text-black">
                                Requested by
                              </p>
                              <p className="text-2xs font-bold lowercase">
                                {selectedTransaction?.paymentRequest?.senderUserSummary.email}
                              </p>
                            </>
                          )}
                        </div>
                      )}
                      {selectedTransaction.type === 'FIXED_AMOUNT_PAYMENT' && (
                        <p className="text-xs font-bold capitalize italic text-black">
                          (Fixed Amount Payment)
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex  mx-4 mt-3 justify-between">
                    <div className="flex w-full flex-col my-1 mx-1">
                      <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">Method</p>
                      {selectedTransaction && (
                        <p className="text-xs font-bold capitalize">
                          {selectedTransaction.paymentMethod?.type === 'BANK_ACCOUNT' ||
                          selectedTransaction.paymentType === 'BANK'
                            ? 'Bank Transfer'
                            : selectedTransaction.paymentMethod?.type === 'CREDIT_CARD' ||
                              selectedTransaction.type === 'WILL_CALL_PAYMENT' ||
                              selectedTransaction.type === 'PAYMENT_REQUEST_PAYMENT'
                            ? `${
                                selectedTransaction?.cardType
                                  ? `${
                                      selectedTransaction?.cardType === 'CREDIT'
                                        ? 'Credit Card'
                                        : 'Debit / Other Card'
                                    }`
                                  : selectedTransaction?.paymentMethod?.cardType
                                  ? `${
                                      selectedTransaction?.paymentMethod?.cardType === 'CREDIT'
                                        ? 'Credit Card'
                                        : 'Debit / Other Card'
                                    }`
                                  : 'Credit/Debit Card'
                              }`
                            : 'Credits'}
                        </p>
                      )}
                      {selectedTransaction &&
                        selectedTransaction?.type !== 'PAYMENT_REQUEST_PAYMENT' &&
                        selectedTransaction?.type !== 'WILL_CALL_PAYMENT' && (
                          <p className="text-xs font-bold capitalize">
                            {selectedTransaction?.paymentMethod?.paymentMethodName ?? ''}
                          </p>
                        )}
                      {selectedTransaction &&
                        (selectedTransaction?.type === 'PAYMENT_REQUEST_PAYMENT' ||
                          selectedTransaction?.type === 'WILL_CALL_PAYMENT') && (
                          <p className="text-xs font-bold capitalize">{`${
                            selectedTransaction?.paymentMethodName ?? ''
                          }`}</p>
                        )}
                      {accountType === 'supplier' &&
                        ((selectedTransaction?.paymentMethod &&
                          selectedTransaction?.paymentMethod?.cardType !== 'CREDIT' &&
                          selectedTransaction?.paymentMethod.type !== 'BANK_ACCOUNT') ||
                          (!selectedTransaction?.paymentMethod &&
                            selectedTransaction?.cardType !== 'CREDIT')) && (
                          <p className="text-2xs italic font-medium mt-1 ">
                            Fee selection does not apply to debit cards, as surcharging the customer
                            on debit cards is not permitted according to federal law.
                          </p>
                        )}
                    </div>
                    <div className="flex w-full flex-col my-1 mx-1">
                      <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">
                        Transaction Status
                      </p>
                      <AppTransactionStatusPill status={selectedTransaction?.transactionStatus} />
                      {selectedTransaction?.transactionStatus === 'refunded' &&
                        selectedTransaction.cardPaymentProvider === 'FISERV' && (
                          <div className="">
                            {selectedTransaction?.amountRefunded && (
                              <p className="text-xs font-bold mt-1 ">
                                {selectedTransaction.amountRefunded <
                                selectedTransaction.totalAmountPaid
                                  ? 'Partial Refund '
                                  : 'Full Refund '}
                                : {formatMoney(selectedTransaction.amountRefunded, 2)} Refunded
                              </p>
                            )}
                            {selectedTransaction?.refundedBy?.email && (
                              <p className="text-2xs font-medium mt-1 ">
                                Refunded by {selectedTransaction.refundedBy.email}
                                {selectedTransaction?.refundedAt
                                  ? ` on ${moment(selectedTransaction.refundedAt).format(
                                      'MMM DD, YYYY [at] h:mm A'
                                    )}`
                                  : ''}
                              </p>
                            )}
                            <p className="text-2xs italic font-medium mt-1 ">
                              Please handle the reconciliation of this in your accounting system
                              accordingly.
                            </p>
                          </div>
                        )}
                    </div>
                    <div className="flex w-full flex-col my-1 mx-1">
                      {accountType === 'supplier' && (
                        <div className="flex flex-col w-full">
                          <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">
                            Reconciliation Status
                          </p>
                          <AppTransactionStatusPill
                            status={selectedTransaction?.reconciliationStatus}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedTransaction?.linkedReleases &&
                    selectedTransaction?.linkedReleases?.length > 1 && (
                      <div className="flex  mx-4 mt-3 justify-between ">
                        <div className="flex flex-col w-full  my-1 mx-1">
                          {selectedTransaction?.linkedReleases &&
                            selectedTransaction?.linkedReleases?.length === 1 && (
                              <div
                                data-tooltip-id={
                                  selectedTransaction?.linkedReleases?.[0]?.status === 'Pending' &&
                                  accountType === 'supplier'
                                    ? 'release-pending-tooltip'
                                    : undefined
                                }>
                                <AppButton
                                  text="View Lien Waiver"
                                  buttonStyles={{ width: '100%' }}
                                  isDisabled={
                                    selectedTransaction?.linkedReleases?.[0]?.status ===
                                      'Pending' && accountType === 'supplier'
                                  }
                                  onClick={() => {
                                    handleViewRelease(
                                      selectedTransaction?.linkedReleases?.[0]?.id ?? ''
                                    );
                                  }}
                                />
                              </div>
                            )}
                          {selectedTransaction?.linkedReleases &&
                            selectedTransaction?.linkedReleases?.length > 1 && (
                              <div
                                className="w-full grid flex-col justify-items-end"
                                ref={containerRef}>
                                <div className="custom-dropdown-container h-fit ">
                                  <AppButton
                                    type="PRIMARY"
                                    buttonStyles={{
                                      padding: '8px',
                                      margin: '0px 0px',
                                      width: '100%',
                                    }}
                                    text="View Lien Waivers"
                                    onClick={handleShowDropdown}
                                    iconPosition="RIGHT"
                                    icon={<ChevronDown color={COLORS.WHITE} />}
                                  />
                                  {showMenu && (
                                    <div className="custom-dropdown-menu h-fit">
                                      <div className={`flex flex-col py-3`}>
                                        {selectedTransaction?.linkedReleases.map((release) => (
                                          <button
                                            data-tooltip-id={
                                              release.status === 'Pending' &&
                                              accountType === 'supplier'
                                                ? 'release-pending-tooltip'
                                                : undefined
                                            }
                                            key={`${release.id}`}
                                            disabled={
                                              release.status === 'Pending' &&
                                              accountType === 'supplier'
                                            }
                                            onClick={() => handleViewRelease(release.id)}
                                            className="w-full flex flex-row py-1.5  px-2 font-medium items-center hover:bg-blue-100">
                                            <div
                                              className="text-sm TEXT_SECONDARY-CLR"
                                              style={{ maxWidth: 200 }}>
                                              {release.job?.name}
                                            </div>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                        <ReactTooltip
                          id={'release-pending-tooltip'}
                          place="top"
                          variant="dark"
                          opacity={'100%'}
                          style={{
                            display: 'flex',
                            background: '#334155',
                            width: '190px',
                            zIndex: '70',
                            borderRadius: '8px',
                            alignItems: 'center',
                            justifyItems: 'center',
                            fontWeight: '600',
                          }}>
                          <span>
                            {selectedTransaction?.linkedReleases &&
                            selectedTransaction?.linkedReleases?.length > 1
                              ? `These lien waiver requests are still pending. They will be sent to you for your sign-off when payment is successfully processed`
                              : `This lien waiver request is still pending. It will be sent to you for your sign-off when payment is successfully processed`}
                          </span>
                        </ReactTooltip>
                      </div>
                    )}
                </div>
                {selectedTransaction?.transactionStatus === 'failed' && error.length > 0 && (
                  <div className="failed-desc-container">
                    <div className="failed-desc-text">
                      <div>{error[0]}</div>
                      <div className="failed-desc-text mt-2">
                        Please reach out to <span>(628) 304-0931</span> for customer support
                      </div>
                    </div>
                  </div>
                )}
                {selectedTransaction && (
                  <div className="w-full   flex px-4 mt-3 ">
                    <div className="flex w-full">
                      <div className="flex w-full justify-between">
                        <div className="  mb-2">
                          <AppButton
                            type="FLAT"
                            buttonStyles={{ padding: '5px', fontSize: '16px', borderRadius: '5px' }}
                            text="Download receipt"
                            onClick={() => {
                              handleDownloadReceipt();
                            }}
                            icon={<Download size={18} />}
                          />
                          <AppButton
                            type="FLAT"
                            buttonStyles={{ padding: '5px', fontSize: '16px', borderRadius: '5px' }}
                            text="Resend receipt"
                            onClick={() => {
                              setIsResendReceiptModalOpen(true);
                            }}
                            icon={<Send size={18} />}
                          />
                          {selectedTransaction.type !== 'WILL_CALL_PAYMENT' &&
                          selectedTransaction.type !== 'FIXED_AMOUNT_PAYMENT' &&
                          filteredInvoice?.length > 0 ? (
                            <AppButton
                              type="FLAT"
                              buttonStyles={{
                                padding: '5px',
                                fontSize: '16px',
                                borderRadius: '5px',
                              }}
                              text="Download all invoices"
                              onClick={() => {
                                handleDownloadAllInvoices();
                              }}
                              icon={<Download size={18} />}
                            />
                          ) : (
                            <div className="mx-4 mt-4 mb-2"></div>
                          )}
                        </div>
                      </div>
                      <div className="flex w-full justify-between">
                        <div className="  mb-2">
                          {selectedTransaction &&
                            accountType === 'supplier' &&
                            selectedTransaction.cardPaymentProvider === 'FISERV' && (
                              <AppButton
                                type="FLAT"
                                buttonStyles={{
                                  padding: '5px',
                                  fontSize: '16px',
                                  borderRadius: '5px',
                                }}
                                text="Refund"
                                onClick={() => {
                                  // handleRefundFiservTransaction();
                                  setIsFiservRefundModalOpen(true);
                                }}
                                isDisabled={
                                  selectedTransaction?.refundPlatformTransactionId ||
                                  selectedTransaction?.transactionStatus === 'refunded' ||
                                  selectedTransaction.transactionStatus !== 'processed' ||
                                  !selectedTransaction.isRefundable
                                    ? true
                                    : false
                                }
                                icon={<ArrowBendDoubleUpLeft size={18} />}
                              />
                            )}
                        </div>
                      </div>
                    </div>

                    {accountType === 'supplier' &&
                      selectedTransaction &&
                      selectedTransaction.paymentMethod?.type === 'BANK_ACCOUNT' && (
                        <div className="flex flex-col w-full  mt-1 mb-2 items-start pl-[157px]">
                          <div className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">
                            Estimated Arrival
                          </div>
                          <div className="text-xs font-bold">
                            {moment(
                              getNextBusinessDay(moment(selectedTransaction?.createdAt), 3)
                            ).format('MMM Do, YYYY')}
                          </div>
                        </div>
                      )}
                  </div>
                )}

                <div className="flex flex-col px-4  grow overflow-y-scroll">
                  <div className="flex w-full flex-col overflow-y-scroll border max-h-96 rounded-xl">
                    <div className="flex border-b px-5 items-center w-full sticky top-0 PRIMARY_50-BG py-3">
                      {columns.map((column) => {
                        return (
                          <div
                            key={column.id}
                            style={{ flex: column.width / totalWidth }}
                            className="flex items-center w-full h-full  pr-4">
                            {column.cellType !== 'SELECT' &&
                              column.id !== 'openAmount' &&
                              column.id !== 'amountPaid' && (
                                <p className="TEXT_SECONDARY-CLR text-xs">{column.name}</p>
                              )}
                            {column.cellType !== 'SELECT' &&
                              (column.id === 'openAmount' || column.id === 'amountPaid') && (
                                <p className="TEXT_SECONDARY-CLR text-xs text-right w-full">
                                  {column.name}
                                </p>
                              )}
                          </div>
                        );
                      })}
                    </div>

                    {selectedInvoices.length < 1 && (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="flex flex-col items-center">
                          <img className="w-3/6 object-contain" src={emptyListImg} alt="" />
                          <p>No invoices</p>
                        </div>
                      </div>
                    )}
                    {invoices.length > 0 &&
                      [...invoices].map((invoice, invoiceIndex) => {
                        return (
                          <div key={invoiceIndex + 'oo'} className={'flex  px-5 my-2 items-center'}>
                            {columns.map((column) => {
                              type ObjectKey = keyof typeof invoice;
                              const columnKey = column.id as ObjectKey;
                              let value = invoice[columnKey]?.toString();
                              if (column.id === 'dueDate') {
                                value = value ? moment(value).utc().format('MM/DD/YYYY') : '';
                              }
                              if (column.id === 'amountPaid') {
                                const amountPaid = invoice?.amountPaid ?? 0;
                                const discountAmount = invoice?.discountAmount ?? 0;
                                const totalAmountPaid = amountPaid + discountAmount;
                                value = formatMoney(totalAmountPaid ?? 0, 2);
                              }
                              if (column.id === 'openAmount') {
                                value = value ? formatMoney(value ?? 0, 2) : '';
                              }
                              if (column.id === 'invoiceNumber') {
                                value = value;
                              }
                              const jobName = `${
                                invoice.quickbooksInvoiceDetails?.quickBooksJob?.jobNumber
                                  ? `${invoice.quickbooksInvoiceDetails?.quickBooksJob?.jobNumber} - `
                                  : ''
                              }${invoice.quickbooksInvoiceDetails?.quickBooksJob?.jobName ?? ''}`;
                              return (
                                <div
                                  key={column.id}
                                  style={{ flex: `${column.width / totalWidth} 1 0%`, minWidth: 0 }}
                                  className="flex items-center w-full h-full   pr-4 ">
                                  {column.cellType === 'VIEW_TEXT' &&
                                    column.id !== 'job' &&
                                    column.id !== 'openAmount' &&
                                    column.id !== 'notes' &&
                                    column.id !== 'amountPaid' && (
                                      <p className={'py-3 text-sm ' + 'TEXT_PRIMARY-CLR'}>
                                        {value}
                                      </p>
                                    )}
                                  {column.cellType === 'VIEW_TEXT' &&
                                    (column.id === 'openAmount' || column.id === 'amountPaid') && (
                                      <p
                                        className={
                                          'py-3 text-xs md:text-sm text-right TEXT_PRIMARY-CLR w-full'
                                        }>
                                        {value}
                                      </p>
                                    )}
                                  {column.cellType === 'VIEW_TEXT' &&
                                    selectedTransaction &&
                                    column.id === 'notes' &&
                                    invoice?.recordType === 'PAYMENT_REQUEST' && (
                                      <p className={'py-3 text-sm  ' + 'TEXT_PRIMARY-CLR'}>
                                        {selectedTransaction.paymentRequest?.notes}
                                      </p>
                                    )}
                                  {column.cellType === 'VIEW_TEXT' &&
                                    selectedTransaction &&
                                    column.id === 'notes' &&
                                    invoice?.recordType === 'WILL_CALL' && (
                                      <p className={'py-3 text-sm  ' + 'TEXT_PRIMARY-CLR'}>
                                        {selectedTransaction.partialPaymentNotes}
                                      </p>
                                    )}
                                  {column.cellType === 'VIEW_TEXT' &&
                                    selectedTransaction &&
                                    column.id === 'notes' &&
                                    invoice?.recordType === 'FIXED_AMOUNT_PAYMENT' && (
                                      <p
                                        className={
                                          'py-3 text-xs md:text-sm  w-full' + 'TEXT_PRIMARY-CLR '
                                        }>
                                        {invoice.notes || ''}
                                      </p>
                                    )}
                                  {column.cellType === 'VIEW_TEXT' &&
                                    selectedTransaction &&
                                    column.id === 'attachments' &&
                                    invoice?.recordType === 'PAYMENT_REQUEST' && (
                                      <div className="flex items-center justify-center flex-wrap ">
                                        {selectedTransaction.paymentRequest?.attachments &&
                                          selectedTransaction.paymentRequest.attachments?.map(
                                            (attachment, i) => {
                                              return (
                                                <a
                                                  key={`${i}-o`}
                                                  className=" m-0.5"
                                                  href={attachment.url}
                                                  target="_blank">
                                                  <File
                                                    weight="fill"
                                                    size={20}
                                                    className="text-blue-400"
                                                  />
                                                </a>
                                              );
                                            }
                                          )}
                                      </div>
                                    )}
                                  {column.cellType === 'VIEW_TEXT' &&
                                    column.id === 'actions' &&
                                    (!invoice?.recordType || invoice?.recordType === 'INVOICE') && (
                                      <div className="">
                                        <Download
                                          size={16}
                                          color={COLORS.PRIMARY_500}
                                          className="ml-2 cursor-pointer"
                                          onClick={() => {
                                            handleDownloadPdf(
                                              invoice?.quickbooksInvoiceDetails?.id ??
                                                invoice?.providerInvoiceId ??
                                                ''
                                            );
                                          }}
                                        />
                                      </div>
                                    )}
                                  {column.id === 'job' &&
                                    invoice?.quickbooksInvoiceDetails?.quickBooksJob && (
                                      <div className={'text-sm w-full px-0.5'}>
                                        <p className="font-bold text-xs ">{jobName}</p>
                                        <p className="text-2xs  ">
                                          {`${
                                            invoice?.quickbooksInvoiceDetails?.quickBooksJob
                                              ?.address || ''
                                          }`}
                                        </p>
                                      </div>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            )}

            {selectedTransaction?.type === 'CREDIT_PAYMENT' && (
              <div>
                <div className="flex flex-col align-center justify-between">
                  <div className="flex  mx-4 mt-3 justify-between">
                    <div className="flex w-full flex-col my-1 mx-1">
                      <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">Date</p>
                      <p className="text-xs font-bold capitalize">
                        {moment(selectedTransaction.createdAt).format('MMM DD, YYYY h:mm A')}
                      </p>
                    </div>
                    <div className="flex w-full flex-col my-1 mx-1">
                      <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">To</p>
                      <p className="text-xs font-bold capitalize">
                        {selectedTransaction?.recipient?.name
                          ? selectedTransaction?.recipient?.name.toLowerCase()
                          : ''}
                      </p>
                    </div>
                    <div className="flex w-full flex-col my-1 mx-1">
                      <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">From</p>
                      <p className="text-xs font-bold capitalize">
                        {`${selectedTransaction?.buyer?.name} - ${selectedTransaction?.buyer?.quickbooksCustomerId}`.toLowerCase()}
                      </p>
                    </div>
                  </div>
                  <div className="flex  mx-4 mt-3 justify-between">
                    <div className="flex w-full flex-col my-1 mx-1">
                      <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">Method</p>
                      {selectedTransaction && (
                        <p className="text-xs font-bold capitalize">
                          {selectedTransaction.paymentMethod?.type === 'BANK_ACCOUNT' ||
                          selectedTransaction.paymentType === 'BANK'
                            ? 'Bank Transfer'
                            : selectedTransaction.paymentMethod?.type === 'CREDIT_CARD'
                            ? `${
                                selectedTransaction?.cardType
                                  ? `${
                                      selectedTransaction?.cardType === 'CREDIT'
                                        ? 'Credit Card'
                                        : 'Debit / Other Card'
                                    }`
                                  : selectedTransaction?.paymentMethod?.cardType
                                  ? `${
                                      selectedTransaction?.cardType === 'CREDIT'
                                        ? 'Credit Card'
                                        : 'Debit / Other Card'
                                    }`
                                  : 'Credit/Debit Card'
                              }`
                            : 'Credits'}
                        </p>
                      )}
                      <p className="text-xs font-bold capitalize">
                        {selectedTransaction?.paymentMethod?.paymentMethodName ?? ''}
                      </p>
                    </div>
                    <div className="flex w-full flex-col my-1 mx-1">
                      <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">
                        Transaction Status
                      </p>
                      <AppTransactionStatusPill status={selectedTransaction?.transactionStatus} />
                    </div>
                    <div className="flex w-full flex-col my-1 mx-1">
                      {accountType === 'supplier' && (
                        <div className="flex flex-col w-full">
                          <p className="text-xs font-semibold mb-1 TEXT_SECONDARY-CLR">
                            Reconciliation Status
                          </p>
                          <AppTransactionStatusPill
                            status={selectedTransaction?.reconciliationStatus}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedTransaction?.linkedReleases &&
                    selectedTransaction?.linkedReleases?.length > 1 && (
                      <div className="flex  mx-4 mt-3 justify-between ">
                        <div className="flex flex-col w-full  my-1 mx-1">
                          {selectedTransaction?.linkedReleases &&
                            selectedTransaction?.linkedReleases?.length === 1 && (
                              <div
                                data-tooltip-id={
                                  selectedTransaction?.linkedReleases?.[0]?.status === 'Pending' &&
                                  accountType === 'supplier'
                                    ? 'release-pending-tooltip'
                                    : undefined
                                }>
                                <AppButton
                                  text="View Lien Waiver"
                                  buttonStyles={{ width: '100%' }}
                                  isDisabled={
                                    selectedTransaction?.linkedReleases?.[0]?.status ===
                                      'Pending' && accountType === 'supplier'
                                  }
                                  onClick={() => {
                                    handleViewRelease(
                                      selectedTransaction?.linkedReleases?.[0]?.id ?? ''
                                    );
                                  }}
                                />
                              </div>
                            )}
                          {selectedTransaction?.linkedReleases &&
                            selectedTransaction?.linkedReleases?.length > 1 && (
                              <div
                                className="w-full grid flex-col justify-items-end"
                                ref={containerRef}>
                                <div className="custom-dropdown-container h-fit ">
                                  <AppButton
                                    type="PRIMARY"
                                    buttonStyles={{
                                      padding: '8px',
                                      margin: '0px 0px',
                                      width: '100%',
                                    }}
                                    text="View Lien Waivers"
                                    onClick={handleShowDropdown}
                                    iconPosition="RIGHT"
                                    icon={<ChevronDown color={COLORS.WHITE} />}
                                  />
                                  {showMenu && (
                                    <div className="custom-dropdown-menu h-fit">
                                      <div className={`flex flex-col py-3`}>
                                        {selectedTransaction?.linkedReleases.map((release) => (
                                          <button
                                            data-tooltip-id={
                                              release.status === 'Pending' &&
                                              accountType === 'supplier'
                                                ? 'release-pending-tooltip'
                                                : undefined
                                            }
                                            key={`${release.id}`}
                                            disabled={
                                              release.status === 'Pending' &&
                                              accountType === 'supplier'
                                            }
                                            onClick={() => handleViewRelease(release.id)}
                                            className="w-full flex flex-row py-1.5  px-2 font-medium items-center hover:bg-blue-100">
                                            <div
                                              className="text-sm TEXT_SECONDARY-CLR"
                                              style={{ maxWidth: 200 }}>
                                              {release.job?.name}
                                            </div>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                        <ReactTooltip
                          id={'release-pending-tooltip'}
                          place="top"
                          variant="dark"
                          opacity={'100%'}
                          style={{
                            display: 'flex',
                            background: '#334155',
                            width: '190px',
                            zIndex: '70',
                            borderRadius: '8px',
                            alignItems: 'center',
                            justifyItems: 'center',
                            fontWeight: '600',
                          }}>
                          <span>
                            {selectedTransaction?.linkedReleases &&
                            selectedTransaction?.linkedReleases?.length > 1
                              ? `These lien waiver requests are still pending. They will be sent to you for your sign-off when payment is successfully processed`
                              : `This lien waiver request is still pending. It will be sent to you for your sign-off when payment is successfully processed`}
                          </span>
                        </ReactTooltip>
                      </div>
                    )}
                </div>
                <div className="mx-6 GREY_300-BORDER border rounded p-4 flex justify-center  mt-2">
                  <AppButton
                    text="View Main Payment"
                    buttonStyles={{
                      marginTop: '4px',
                      marginBottom: '4px',
                    }}
                    onClick={() =>
                      handleViewLinkedPayment(selectedTransaction?.linkedDillTransactionId ?? '')
                    }
                  />
                </div>
                {selectedTransaction?.transactionStatus === 'failed' && error.length > 0 && (
                  <div className="failed-desc-container">
                    <div className="failed-desc-text">
                      <div>{error[0]}</div>
                      <div className="failed-desc-text mt-2">
                        Please reach out to <span>(628) 304-0931</span> for customer support
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* {paymentType === 'FEES' && (
              <div className="w-full justify-between flex">
                <div />
                <div className="self-center mx-6 mt-4">
                  <AppTransactionStatusPill status={selectedTransaction?.transactionStatus} />
                </div>
              </div>
            )} */}
            {selectedTransaction?.transactionStatus === 'failed' &&
              error.length > 0 &&
              selectedTransaction?.type !== 'PAYMENT' &&
              selectedTransaction?.type !== 'WILL_CALL_PAYMENT' &&
              selectedTransaction?.type !== 'PAYMENT_REQUEST_PAYMENT' && (
                <div className="failed-desc-container">
                  <div className="failed-desc-text">
                    <div>{error[0]}</div>
                    <div className="failed-desc-text mt-2">
                      Please reach out to <span>(628) 304-0931</span> for customer support
                    </div>
                  </div>
                </div>
              )}
            {selectedTransaction?.partialPaymentReason && (
              <div className="flex flex-col w-full  mb-1  px-2 md:px-5">
                <div className="w-full px-2 md:px-4 py-2 PRIMARY_50-BG  my-4 rounded-xl">
                  <div className="flex flex-col md:flex-row   w-full">
                    <div className="text-xs md:text-sm font-bold mr-2 text-nowrap md:w-1/3">
                      Short Payment Reason:
                    </div>
                    <div className="text-xs md:text-sm  md:w-2/3">
                      {selectedTransaction?.partialPaymentReason}
                    </div>
                  </div>

                  {selectedTransaction?.partialPaymentNotes &&
                    selectedTransaction?.partialPaymentNotes.length > 0 && (
                      <div className="flex flex-col md:flex-row mt-2">
                        <div className="text-xs md:text-sm font-bold md:mr-2 text-nowrap md:w-1/3">
                          Short Payment Notes:
                        </div>
                        <div className="text-xs md:text-sm md:w-2/3">
                          {selectedTransaction?.partialPaymentNotes}
                        </div>
                      </div>
                    )}
                  {selectedTransaction?.partialPaymentAttachments &&
                    selectedTransaction?.partialPaymentAttachments.length > 0 && (
                      <div className="flex flex-col md:flex-row mt-2 rounded-xl ">
                        <div className="text-xs md:text-sm font-bold mr-2  text-nowrap w-1/3">
                          Short Payment Attachments:
                        </div>
                        <div className="flex flex-col  w-2/3">
                          {selectedTransaction?.partialPaymentAttachments?.map(
                            (partialPaymentAttachment, i) => {
                              return (
                                <div key={`${i}-oo`} className="text-xs md:text-sm text-blue-500">
                                  <a href={partialPaymentAttachment.url} target="_blank">
                                    {partialPaymentAttachment.name}
                                  </a>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}
            {selectedTransaction?.amountRefunded && selectedTransaction?.amountRefunded > 0 && (
              <div className="flex flex-col w-full  mb-1  px-2 md:px-5">
                <div className="w-full px-2 md:px-4 py-2 PRIMARY_50-BG  my-4 rounded-xl">
                  <div className="flex flex-col md:flex-row   w-full">
                    <div className="text-xs md:text-sm font-bold mr-2 text-nowrap md:w-1/3">
                      Amount refunded:
                    </div>
                    <div className="text-xs md:text-sm  md:w-2/3">
                      {formatMoney(selectedTransaction.amountRefunded, 2)}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-col w-full  mb-3">
              {selectedTransaction?.type !== 'CREDIT_PAYMENT' && (
                <div className="w-full px-4">
                  {selectedTransaction?.type !== 'WITHDRAW' && (
                    <div className="flex flex-col justify-between  mt-4 rounded-xl PRIMARY_50-BG px-5 py-4">
                      <div className="flex flex-row justify-between  mb-2 ">
                        <div className="amount-label-text">Amount</div>
                        <div className="amount-text">
                          {`${
                            selectedTransaction
                              ? formatMoney(
                                  selectedTransaction?.totalAmountPaid -
                                    buyerFees -
                                    buyerFiservFees +
                                    creditAmount +
                                    discount -
                                    creditDiscount,
                                  2
                                )
                              : ''
                          }`}
                        </div>
                      </div>
                      {(discount > 0 || creditDiscount > 0) && renderDiscountDropDown()}
                      {creditAmount > 0 && renderCreditDropDown()}
                      {(creditAmount > 0 || discount > 0) && (
                        <div className="flex flex-row justify-between w-full pb-2 border-b mb-2">
                          <div className="text-base GREY_500-CLR">
                            Amount After Credits And Discounts
                          </div>
                          <div className="text-sm font-bold">
                            {`${formatMoney(
                              (selectedTransaction?.totalAmountPaid || 0) -
                                buyerFees -
                                buyerFiservFees,
                              2
                            )}`}
                          </div>
                        </div>
                      )}
                      {(buyerFees !== 0 || buyerFiservFees !== 0) && (
                        <div className={`flex flex-row justify-between w-full pb-2`}>
                          <div className="detailText">{`${
                            accountType === 'supplier' ? 'Buyer Fee' : 'Your Fee'
                          }`}</div>
                          {buyerFees !== 0 && (
                            <div className="text-sm font-bold">{`+${formatMoney(
                              buyerFees,
                              2
                            )}`}</div>
                          )}
                          {buyerFiservFees !== 0 && (
                            <div className="text-sm font-bold">{`+${formatMoney(
                              buyerFiservFees,
                              2
                            )}`}</div>
                          )}
                        </div>
                      )}
                      {supplierFees + buyerFees !== 0 && accountType === 'supplier' && (
                        <div className="flex flex-row justify-between w-full pb-2 ">
                          <div className="detailText">{`${
                            accountType === 'supplier' ? 'Your Fee' : 'Supplier Fee'
                          }`}</div>
                          <div className="text-sm font-bold">{`-${formatMoney(
                            supplierFees + buyerFees,
                            2
                          )}`}</div>
                        </div>
                      )}
                      <div className="flex flex-row justify-between w-full pt-2 border-t">
                        <div className="text-sm font-bold">Total Payment</div>
                        <div className="totalText">{`${
                          selectedTransaction
                            ? accountType === 'supplier'
                              ? `${formatMoney(
                                  selectedTransaction?.totalAmountPaid - buyerFees - supplierFees,
                                  2
                                )}`
                              : `${formatMoney(selectedTransaction?.totalAmountPaid, 2)}`
                            : ''
                        }`}</div>
                      </div>
                    </div>
                  )}
                  {selectedTransaction?.type === 'WITHDRAW' && (
                    <div className="flex flex-col justify-between  mt-4 rounded-xl PRIMARY_50-BG px-5 py-4">
                      <div className="flex flex-row justify-between w-full pb-2 ">
                        <div className="detailText">Amount</div>
                        <div className="text-sm font-bold">{`${
                          selectedTransaction
                            ? formatMoney(
                                selectedTransaction?.totalAmountPaid +
                                  (selectedTransaction?.facilitationFee ?? 0),
                                2
                              )
                            : ''
                        }`}</div>
                      </div>{' '}
                      {selectedTransaction?.facilitationFee !== 0 && (
                        <div className="flex flex-row justify-between w-full pb-2 border-b">
                          <div className="detailText">Fee</div>
                          <div className="text-sm font-bold">{`-${formatMoney(
                            selectedTransaction?.facilitationFee ?? 0,
                            2
                          )}`}</div>
                        </div>
                      )}
                      <div className="flex flex-row justify-between w-full pt-2">
                        <div className="text-sm font-bold">Total Payment</div>
                        <div className="totalText">{`${
                          selectedTransaction
                            ? formatMoney(selectedTransaction?.totalAmountPaid, 2)
                            : ''
                        }`}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <hr className="h-px my-2 bg-gray-200 border-0 dark:bg-gray-200"></hr>
              <div className="flex flex-row self-end px-6">
                <AppButton
                  text="Close"
                  buttonStyles={{
                    marginTop: '4px',
                    marginBottom: '4px',
                  }}
                  onClick={handleCloseModal}
                  type="TERTIARY"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
